import { GenerateImageOptions, GenerateImageRendererOptions } from "@centricsoftware/model-viewer";
import JsonResponse from "csi/comm/JsonResponse";
import rootStore from "csi/state/rootStore";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import { useLocalReactionStore } from "designSystem/hooks";
import { FlexColumn } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useMemo } from "react";
import Cache from "src/csi/state/Cache";
import { useSnapshotTemplateDialogStore } from "../dialogs/SnapshotTemplateDialogStore";
import { SnapshotTemplateStore, SnapshotTemplateStoreContext } from "../SnapshotTemplateStore";
import { AutoPopulateOptions, SnapshotTemplateId } from "../snapshotUtils";
import BackgroundSettings from "./BackgroundSettings";
import CameraPositions from "./CameraPositions";
import ImageSettings from "./ImageSettings";
import ImageType from "./ImageType";
import MaterialSettings from "./MaterialSettings";
import { TemplateDescription, TemplateName, TemplateSelect } from "./TemplateSettings";
import { VariantsIncludeAll, VariantsSelect } from "./VariantSettings";
import EnvironmentLighting from "./EnvironmentLighting";
import ProductTypes from "./ProductTypes";
import { cnl } from "csi/typings";
import { useLocalGenerateImageStore } from "csi/threeD/snapshots/GenerateImagesStore";

interface TemplateFormProps extends AutoPopulateOptions {
	/** Actions */
	actions?: React.ReactNode;
	
	/** id to determine if style or material options should be rendered */
	id: SnapshotTemplateId;
	
	/** determine if options should be auto populated - true when selecting from the 3D viewer */
	isTemplateAutoPopulated?: boolean;
	
	/** Template to copy */
	isCopyTemplate?: boolean;

	/** Template to edit */
	selectedTemplate?: cnl;

	/** Callback to set the ImageOptions  */
	setImageOptionsCallback: (options: GenerateImageOptions) => void;

	/** Callback to set the RendererOptions  */
	setRendererOptionsCallback: (options: GenerateImageRendererOptions) => void;

	/** Callback to set template options fields */
	setTemplateOptionsCallback?: (options) => void;

	/** Callback to set if a user can save a template after making modifications to a current template */
	setUserCanSaveTemplate?: (canSave: boolean) => void;

	/** Callback to set if the data has changed */
	setDataHasChanged?: (response: JsonResponse) => void;
}

class LocalConfigStore {
	readonly id: SnapshotTemplateId;

	constructor(id: SnapshotTemplateId) {
		this.id = id;
		this.fetchAndCacheTemplateData();
	}

	/** cache to store environment & Style type queries */
	localCache: Cache = new Cache();

	/** async fetching of Environment Urls */
	fetchEnvironmentUrls = async (): Promise<void> => {
		try {
			const search = new Search()
				.byType("ThreeDEnvironment");
			const query = search.getQuery();
			query.Attr("Active", xml.OP_EQUALS, true);
			search.getSortQuery()
				.SortByNode(xml.PROP_CREATE_TIME, null, true);

			const response = await search.getRequest()
				.go();

			runInAction(() => {
				this.localCache.cacheResponse(response);
			});
		} catch (err) {
			console.error("fetchEnvironmentUrls: ", err);
		}
	};

	/** async fetching of Style or Material Types */
	fetchTypes = async (): Promise<void> => {
		const typeToFetch = this.id === SnapshotTemplateId.Material ? "MaterialType" : "StyleType";
		try {
			const search = new Search()
				.byType(typeToFetch);
			const query = search.getQuery();
			query.Attr("Available", xml.OP_EQUALS, true, "Child:Config")
				.Attr("Published", xml.OP_EQUALS, true);

			const response = await search.getRequest()
				.go();

			runInAction(() => {
				this.localCache.cacheResponse(response);
			});
		} catch (err) {
			console.error("fetchTypes Error: ", err);
		};
	};

	/** async fetching of Snapshot Templates */
	fetchSnapshotTemplates = async (): Promise<void> => {
		const templatesToFetch = this.id === SnapshotTemplateId.Material ? "MaterialSnapshotTemplate" : "StyleSnapshotTemplate";
		try {
			const search = new Search()
				.byType(templatesToFetch);
			const response = await search.getRequest()
				.go();
			runInAction(() => {
				this.localCache.cacheResponse(response);
			});
		} catch (err) {
			console.error("fetchSnapshotTemplates Error: ", err);
		}
	};

	/** async fetching of Camera Position data */
	fetchCameraPositions = async (): Promise<void> => {
		try {
			const search = new Search()
				.byType("CameraPosition");
			const query = search.getQuery();
			query.Attr("Active", xml.OP_EQUALS, true);
			const response = await search.getRequest()
				.go();
			runInAction(() => {
				this.localCache.cacheResponse(response);
			});
		} catch (err) {
			console.error("fetchCameraPositions: ", err);
		}
	};

	/** Fetch and cache data needed for the snapshot template store */
	fetchAndCacheTemplateData = async (): Promise<void> => {
		const { fetchEnvironmentUrls, fetchTypes, fetchSnapshotTemplates, fetchCameraPositions } = this;
		await Promise.all([
			fetchEnvironmentUrls(),
			fetchTypes(),
			fetchSnapshotTemplates(),
			fetchCameraPositions(),
		]);
	};
}

const TemplateForm: FunctionComponent<TemplateFormProps> = observer((props) => {
	const generateImagesStore = useLocalGenerateImageStore();
	const dialogStore = useSnapshotTemplateDialogStore();
	const {
		id,
		isTemplateAutoPopulated,
		setImageOptionsCallback,
		setRendererOptionsCallback,
		setTemplateOptionsCallback,
		setUserCanSaveTemplate,
		setDataHasChanged,
		autoPopulatedRendererOptions,
		autoPopulatedImageOptions,
		autoPopulatedTemplateOptions,
		actions,
		selectedTemplate,
		isCopyTemplate,
	} = props;
	const locale = useLocale();
	const localStore = useMemo(() => new LocalConfigStore(id), []);
	const { localCache } = localStore;
	const { threeDStore } = rootStore;

	const templateStore = useLocalReactionStore(() => new SnapshotTemplateStore(
		id,
		localCache,
		locale,
		setImageOptionsCallback,
		setRendererOptionsCallback,
		setTemplateOptionsCallback,
		setUserCanSaveTemplate,
		setDataHasChanged,
		threeDStore,
		generateImagesStore,
		dialogStore,
		autoPopulatedRendererOptions,
		autoPopulatedImageOptions,
		autoPopulatedTemplateOptions,
		selectedTemplate,
		isCopyTemplate,
	));

	const isMaterialTemplate = id === SnapshotTemplateId.Material;
	return (
		!!templateStore && (
			<SnapshotTemplateStoreContext.Provider value={templateStore}>
				{
					isTemplateAutoPopulated ? (
						<FlexColumn p={3} paddingTop={2}>
							<TemplateSelect />
							<ImageType />
							{
								isMaterialTemplate ? (
									<MaterialSettings />
								) : (
									<VariantsSelect />
								)
							}
							<ImageSettings />
							<CameraPositions />
							<EnvironmentLighting />
							<BackgroundSettings />
						</FlexColumn>
					) : (
						<FlexColumn
							marginBottom="40px"
							p={2}
							paddingTop={1}
						>
							<TemplateName />
							<TemplateDescription />
							<ProductTypes />
							<ImageType />
							{
								isMaterialTemplate ? (
									<MaterialSettings forceVisible />
								) : (
									<VariantsIncludeAll />
								)
							}
							<ImageSettings />
							<CameraPositions />
							<EnvironmentLighting />
							<BackgroundSettings />
						</FlexColumn>
					)
				}
				{actions}
			</SnapshotTemplateStoreContext.Provider>
		));
});

export default TemplateForm;